// Clinic Template System for Electronic Clinics
// Templates are tiered based on subscription plans:
// - Professional (3 templates): classic-medical, modern-teal, corporate-dark
// - Advanced (6 templates): All templates
// - Enterprise (6 templates + full customization)

export interface ClinicTemplate {
  id: string;
  name: string;
  nameAr: string;
  description: string;
  tier: 'professional' | 'advanced' | 'enterprise';
  previewImage?: string;
  styles: {
    // Overall theme
    theme: 'light' | 'dark' | 'teal' | 'warm' | 'navy' | 'minimal' | 'sky';
    // Header/Cover styles
    headerStyle: 'full-image' | 'gradient' | 'solid' | 'split' | 'minimal' | 'wave';
    headerHeight: 'short' | 'medium' | 'tall' | 'hero';
    headerOverlay: 'none' | 'light' | 'dark' | 'gradient';
    // Card styles
    cardStyle: 'elevated' | 'flat' | 'bordered' | 'glass' | 'floating' | 'minimal';
    cardRadius: 'none' | 'small' | 'medium' | 'large' | 'full';
    // Layout
    layout: 'centered' | 'wide' | 'compact' | 'asymmetric';
    // Profile card position & style
    profilePosition: 'floating' | 'inline' | 'sidebar' | 'hero-overlay' | 'top-bar';
    profileStyle: 'card' | 'minimal' | 'banner' | 'circular' | 'horizontal';
    // Products grid style
    productsLayout: 'grid' | 'list' | 'featured' | 'cards-with-image' | 'compact';
    // Background
    backgroundColor: 'white' | 'light-gray' | 'cream' | 'dark' | 'teal-tint' | 'navy';
    // Typography
    fontStyle: 'modern' | 'classic' | 'elegant' | 'bold' | 'minimal';
    // Animations
    animations: 'full' | 'subtle' | 'none';
    // Special effects
    effects: string[];
    // Decorative elements
    decorations: 'none' | 'medical-icons' | 'organic-shapes' | 'geometric' | 'dots' | 'waves';
  };
}

export const clinicTemplates: ClinicTemplate[] = [
  // === PROFESSIONAL TIER (3 templates) ===
  
  // 1. Classic Medical - Clean, professional white theme (like Medilo)
  {
    id: 'classic-medical',
    name: 'Classic',
    nameAr: 'كلاسيكي',
    description: 'تصميم نظيف واحترافي مناسب للأفراد - خلفية بيضاء مع بطاقات مرتفعة',
    tier: 'professional',
    styles: {
      theme: 'light',
      headerStyle: 'gradient',
      headerHeight: 'medium',
      headerOverlay: 'light',
      cardStyle: 'elevated',
      cardRadius: 'medium',
      layout: 'centered',
      profilePosition: 'floating',
      profileStyle: 'card',
      productsLayout: 'grid',
      backgroundColor: 'white',
      fontStyle: 'classic',
      animations: 'subtle',
      effects: ['shadow-lg', 'hover-lift'],
      decorations: 'none',
    },
  },
  
  // 2. Wellness Warm - Cream/warm colors for nutrition clinics - SIDEBAR PROFILE (UNIQUE)
  {
    id: 'wellness-warm',
    name: 'Modern',
    nameAr: 'عصري',
    description: 'تصميم دافئ بألوان طبيعية مع بروفايل جانبي - مثالي لعيادات التغذية والصحة العامة',
    tier: 'professional',
    styles: {
      theme: 'warm',
      headerStyle: 'gradient',
      headerHeight: 'hero',
      headerOverlay: 'gradient',
      cardStyle: 'floating',
      cardRadius: 'full',
      layout: 'asymmetric',
      profilePosition: 'sidebar',
      profileStyle: 'card',
      productsLayout: 'cards-with-image',
      backgroundColor: 'cream',
      fontStyle: 'elegant',
      animations: 'full',
      effects: ['warm-glow', 'organic-border'],
      decorations: 'none',
    },
  },

  // 3. Corporate Dark - Top navbar with hero image (like Zid/SFDA)
  {
    id: 'corporate-dark',
    name: 'Corporate',
    nameAr: 'مؤسسي',
    description: 'تصميم رسمي بشريط علوي وهيدر كبير مع صورة خلفية - مثالي للمراكز الطبية',
    tier: 'professional',
    styles: {
      theme: 'navy',
      headerStyle: 'full-image',
      headerHeight: 'hero',
      headerOverlay: 'gradient',
      cardStyle: 'bordered',
      cardRadius: 'small',
      layout: 'wide',
      profilePosition: 'top-bar',
      profileStyle: 'banner',
      productsLayout: 'grid',
      backgroundColor: 'white',
      fontStyle: 'bold',
      animations: 'subtle',
      effects: ['accent-border', 'cta-highlight'],
      decorations: 'none',
    },
  },
];

// Get templates available for a subscription plan
export function getTemplatesForPlan(planName: string): ClinicTemplate[] {
  return clinicTemplates; // All 3 templates available for all plans
}

// Check if a template is available for a plan
export function isTemplateAvailableForPlan(templateId: string, planName: string): boolean {
  const availableTemplates = getTemplatesForPlan(planName);
  return availableTemplates.some(t => t.id === templateId);
}

// Get template by ID
export function getTemplateById(templateId: string): ClinicTemplate | undefined {
  return clinicTemplates.find(t => t.id === templateId);
}

// Check if a template ID is a custom (non-standard) template
export function isCustomTemplate(templateId: string): boolean {
  return !clinicTemplates.some(t => t.id === templateId);
}

// Check if plan allows full customization
export function canFullyCustomize(planName: string): boolean {
  const normalizedPlan = planName.toLowerCase();
  return normalizedPlan.includes('enterprise') || normalizedPlan.includes('المنشآت');
}

// Get template preview colors for selector
export function getTemplatePreviewColors(templateId: string): { bg: string; accent: string; text: string } {
  const template = getTemplateById(templateId);
  if (!template) return { bg: '#ffffff', accent: '#8a8a8a', text: '#1f2937' };
  
  switch (template.styles.theme) {
    case 'dark':
      return { bg: '#111827', accent: '#b8860b', text: '#ffffff' };
    case 'warm':
      return { bg: '#fffbf5', accent: '#d97706', text: '#78350f' };
    case 'navy':
      return { bg: '#1e3a5f', accent: '#3b82f6', text: '#ffffff' };
    default:
      return { bg: '#ffffff', accent: '#3b82f6', text: '#1f2937' };
  }
}
